import { LOCALE_QUERY_PARAM, LOCALE_STORAGE_KEY, isSupportedLocale, type Locale } from "./locales";

/**
 * 표시 언어 결정 (ADR 0025 · Issue #13)
 * --------------------------------------------------------------------------
 * 우선순위는 **결정적**이며 한 곳에만 적습니다.
 *
 *   1. URL의 `?lang=` — 링크 하나로 재현되어야 합니다.
 *   2. 이 브라우저에 저장된 선택 — 사용자가 직접 고른 값입니다.
 *   3. 브라우저 언어(`navigator.languages` 순서) — 고르지 않은 사용자의 힌트입니다.
 *   4. 운영자 기본값 — 인증된 `GET /api/v1/ui-config`가 준 값입니다.
 *
 * 어느 단계든 **지원 목록 안의 값만** 이길 수 있습니다. 목록 밖 값은 없는 것으로
 * 보고 다음 단계로 넘어갑니다 — 그래야 `?lang=ja`가 빈 화면을 만들지 않습니다.
 */
export interface LocaleInputs {
  /** 현재 URL의 쿼리 문자열. `?`가 있어도 없어도 됩니다. */
  search?: string;
  /** 저장된 선택. 읽지 못했으면 null입니다. */
  stored?: string | null;
  /** 브라우저 언어 목록. `navigator.languages` 순서를 그대로 줍니다. */
  browser?: readonly string[];
  /** 서버가 준 운영자 기본값. 아직 모르면 undefined입니다. */
  operatorDefault?: string;
  /** 이 배포가 제공하는 언어. 서버 응답이 오기 전에는 계약 기본값입니다. */
  supported: readonly Locale[];
}

/** 결정에 쓰인 단계. 진단과 테스트가 "왜 이 언어인가"를 볼 수 있어야 합니다. */
export type LocaleSource = "url" | "stored" | "browser" | "operator" | "fallback";

export interface LocaleDecision {
  locale: Locale;
  source: LocaleSource;
}

function pick(value: string | null | undefined, supported: readonly Locale[]): Locale | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toLowerCase();
  if (!isSupportedLocale(normalized)) return undefined;
  return supported.includes(normalized) ? normalized : undefined;
}

/**
 * `ko-KR` 같은 지역 태그를 기본 언어 부분으로 줄입니다.
 * 이 단계는 **브라우저 힌트에만** 적용합니다 — URL·저장값·운영자 값은 우리가
 * 발급한 값이라 정확히 일치해야 합니다.
 */
function pickBrowser(tags: readonly string[] | undefined, supported: readonly Locale[]): Locale | undefined {
  for (const tag of tags ?? []) {
    const exact = pick(tag, supported);
    if (exact) return exact;
    const base = pick(tag.split("-", 1)[0], supported);
    if (base) return base;
  }
  return undefined;
}

export function resolveLocale(inputs: LocaleInputs): LocaleDecision {
  const supported = inputs.supported;
  const url = pick(new URLSearchParams(inputs.search ?? "").get(LOCALE_QUERY_PARAM), supported);
  if (url) return { locale: url, source: "url" };
  const stored = pick(inputs.stored, supported);
  if (stored) return { locale: stored, source: "stored" };
  const browser = pickBrowser(inputs.browser, supported);
  if (browser) return { locale: browser, source: "browser" };
  const operator = pick(inputs.operatorDefault, supported);
  if (operator) return { locale: operator, source: "operator" };
  /* 지원 목록은 서버가 비워 보낼 수 없습니다(계약이 최소 1개를 강제). 그래도
     방어적으로 첫 항목을 씁니다 — 언어가 없는 화면을 만들지 않습니다. */
  return { locale: supported[0]!, source: "fallback" };
}

/** 저장된 선택을 읽습니다. 저장소가 막힌 브라우저에서도 던지지 않습니다. */
export function readStoredLocale(): string | null {
  try {
    return window.localStorage.getItem(LOCALE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** 선택을 저장합니다. 실패해도 화면은 그대로 동작합니다(이번 세션만 유지). */
export function writeStoredLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* 사생활 모드·저장소 한도. 선택은 이번 세션에만 남습니다. */
  }
}
